import { useMotionValue } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { signCarouselSlideUrl, type ReadyCarouselConfig, type ReadyCarouselSlide } from './CarouselRegistry'

type SequenceOptions = {
  config: ReadyCarouselConfig
  onComplete?: () => void
}

export const useCarouselSequence = ({ config, onComplete }: SequenceOptions) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [direction, setDirection] = useState(1)
  const progress = useMotionValue(0)
  const videoPlayheads = useRef(new Map<string, number>())
  const activeVideo = useRef<HTMLVideoElement | null>(null)
  // Track the in-flight loadedmetadata resume listener so we can detach it
  // when the same element is rebound with null or replaced by a new element.
  // The previous shape leaked the listener and could cause an off-screen
  // video to start playing after the user already advanced.
  const activeVideoListener = useRef<{ video: HTMLVideoElement; handler: () => void } | null>(null)
  const completed = useRef(false)
  // Per-slide signed URL refresh: every slide entry re-issues the signer call
  // so URLs never expire mid-playback (decided over a "track expiry and only
  // refresh when stale" approach for robustness). The current slide's URL is
  // awaited inline; the next slide's URL is prefetched in parallel during the
  // dwell so the swap is seamless.
  const [liveUrls, setLiveUrls] = useState<Record<string, string>>(() =>
    Object.fromEntries(config.slides.map((slide) => [slide.id, slide.assetUrl])),
  )
  const inFlightUrls = useRef(new Map<string, Promise<string>>())
  const slidesRef = useRef(config.slides)

  // Identity-reset: if the config's slides array changes by reference (server
  // mutation, journey switch within the same provider), drop in-flight state
  // so we don't render activeIndex past the new array end.
  useEffect(() => {
    if (slidesRef.current === config.slides) return
    slidesRef.current = config.slides
    setActiveIndex(0)
    setDirection(1)
    progress.set(0)
    completed.current = false
    videoPlayheads.current.clear()
    inFlightUrls.current.clear()
    setLiveUrls(Object.fromEntries(config.slides.map((slide) => [slide.id, slide.assetUrl])))
  }, [config.slides, progress])

  const refreshSlideUrl = useCallback((slide: ReadyCarouselSlide): Promise<string> => {
    const existing = inFlightUrls.current.get(slide.id)
    if (existing) return existing
    const pending = signCarouselSlideUrl(slide, config.journeyId).then((nextUrl) => {
      setLiveUrls((current) => (current[slide.id] === nextUrl ? current : { ...current, [slide.id]: nextUrl }))
      return nextUrl
    }).finally(() => {
      // Allow a future entry to refresh again. We deliberately don't keep the
      // promise around past completion — the next slide entry should issue a
      // fresh signer call (server URL TTL is 10 min).
      inFlightUrls.current.delete(slide.id)
    })
    inFlightUrls.current.set(slide.id, pending)
    return pending
  }, [config.journeyId])

  const detachVideoListener = useCallback(() => {
    const pending = activeVideoListener.current
    if (!pending) return
    pending.video.removeEventListener('loadedmetadata', pending.handler)
    activeVideoListener.current = null
  }, [])

  const preserveActiveVideo = useCallback(() => {
    const video = activeVideo.current
    const slide = config.slides[activeIndex]
    if (!video || slide?.type !== 'video') return
    videoPlayheads.current.set(slide.id, video.currentTime)
    video.pause()
  }, [activeIndex, config.slides])

  const goTo = useCallback((nextIndex: number, nextDirection: number) => {
    if (nextIndex === activeIndex || nextIndex < 0 || nextIndex >= config.slides.length) return false
    preserveActiveVideo()
    detachVideoListener()
    setDirection(nextDirection)
    setActiveIndex(nextIndex)
    progress.set(0)
    return true
  }, [activeIndex, config.slides.length, detachVideoListener, preserveActiveVideo, progress])

  const next = useCallback(() => {
    const nextIndex = activeIndex + 1
    if (nextIndex < config.slides.length) return goTo(nextIndex, 1)
    if (config.autoLoop) return goTo(0, 1)
    return false
  }, [activeIndex, config.autoLoop, config.slides.length, goTo])

  const previous = useCallback(() => {
    const previousIndex = activeIndex - 1
    if (previousIndex >= 0) return goTo(previousIndex, -1)
    if (config.autoLoop) return goTo(config.slides.length - 1, -1)
    return false
  }, [activeIndex, config.autoLoop, config.slides.length, goTo])

  // On every slide entry: refresh the active slide's URL synchronously
  // (await ensures the <video>/<img> swap uses a fresh URL) and prefetch the
  // next slide's URL in parallel so the next entry is instant.
  useEffect(() => {
    const slide = config.slides[activeIndex]
    if (!slide) return
    void refreshSlideUrl(slide)
    const upcomingIndex = activeIndex + 1 < config.slides.length
      ? activeIndex + 1
      : config.autoLoop ? 0 : null
    if (upcomingIndex !== null && upcomingIndex !== activeIndex) {
      void refreshSlideUrl(config.slides[upcomingIndex])
    }
  }, [activeIndex, config.autoLoop, config.slides, refreshSlideUrl])

  // Auto-advance + progress meter. Pauses cleanly on visibilitychange so a
  // tabbed-out user doesn't return to find the carousel mid-final-slide.
  useEffect(() => {
    const slide = config.slides[activeIndex]
    if (!slide) return
    let elapsedBeforePause = 0
    let segmentStart = performance.now()
    let frame = 0
    let timer: number | null = null
    let cancelled = false

    const finishIfLast = () => {
      if (activeIndex === config.slides.length - 1 && !config.autoLoop) {
        progress.set(1)
        if (!completed.current) {
          completed.current = true
          onComplete?.()
        }
        return true
      }
      return false
    }
    const remaining = () => Math.max(0, slide.dwellMs - elapsedBeforePause - (performance.now() - segmentStart))
    const updateProgress = (now: number) => {
      progress.set(Math.min(1, (elapsedBeforePause + (now - segmentStart)) / slide.dwellMs))
      if (!cancelled && remaining() > 0) frame = requestAnimationFrame(updateProgress)
    }
    const arm = () => {
      segmentStart = performance.now()
      frame = requestAnimationFrame(updateProgress)
      timer = window.setTimeout(() => {
        timer = null
        if (cancelled) return
        if (finishIfLast()) return
        void next()
      }, remaining())
    }
    const pause = () => {
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
      cancelAnimationFrame(frame)
      elapsedBeforePause += performance.now() - segmentStart
      const video = activeVideo.current
      if (slide.type === 'video' && video) {
        videoPlayheads.current.set(slide.id, video.currentTime)
        video.pause()
      }
    }
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') pause()
      else if (!cancelled) arm()
    }

    arm()
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
      if (timer !== null) window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [activeIndex, config.autoLoop, config.slides, next, onComplete, progress])

  const bindVideo = useCallback((video: HTMLVideoElement | null) => {
    // Pause the previous active video before overwriting the ref. Without
    // this, fast advances can leave audio + frames playing on a video
    // element that's still in the DOM during AnimatePresence's exit phase.
    if (activeVideo.current && activeVideo.current !== video) activeVideo.current.pause()
    detachVideoListener()
    activeVideo.current = video
    if (!video) return
    const slide = config.slides[activeIndex]
    if (!slide) return
    const savedTime = videoPlayheads.current.get(slide.id)
    const resume = () => {
      if (savedTime !== undefined && Number.isFinite(savedTime)) video.currentTime = savedTime
      void video.play().catch(() => {})
    }
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) resume()
    else {
      video.addEventListener('loadedmetadata', resume, { once: true })
      activeVideoListener.current = { video, handler: resume }
    }
  }, [activeIndex, config.slides, detachVideoListener])

  useEffect(() => () => {
    detachVideoListener()
    activeVideo.current?.pause()
    videoPlayheads.current.clear()
    inFlightUrls.current.clear()
  }, [detachVideoListener])

  const baseSlide = config.slides[activeIndex]
  // Compose the slide with the live (refreshed) URL — falls back to the
  // initial config URL until the first refresh resolves.
  const activeSlide = useMemo<ReadyCarouselSlide | undefined>(() => {
    if (!baseSlide) return undefined
    const liveUrl = liveUrls[baseSlide.id]
    return liveUrl && liveUrl !== baseSlide.assetUrl
      ? { ...baseSlide, assetUrl: liveUrl }
      : baseSlide
  }, [baseSlide, liveUrls])

  return {
    activeIndex,
    activeSlide,
    direction,
    progress,
    next,
    previous,
    bindVideo,
  }
}
